import React, { useState, useEffect, useMemo } from 'react'

import {
  formatCurrency,
  formatNumber,
  getPaymentOption,
  getAssignmentPaymentMethodKeys,
  getAssignmentPaymentMethodLabel,
  isCardLikeAssignmentPaymentMethod,
  getAmountDue,
} from '../../../utilities/helpers'

import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Stack from 'react-bootstrap/Stack'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'

import SelectedTickets from '../SelectedTickets'
import { PaymentPlan } from './PaymentPlan'
import { SelectPaymentDueDate } from '../SelectPaymentDueDate'

import { constants } from '@blocktickets/shared-utils'
import AuthService from '../../../utilities/services/auth.service'
const { PAYMENT_TYPE } = constants

export default function Pricing({
  selectedTickets,
  isPackage,
  price,
  setPrice,
  priceTiers,
  paymentMethod,
  setPaymentMethod,
  allowEditPrice,
  pricingObject,
  paymentSchedule,
  setPaymentSchedule,
  installmentMonths,
  setInstallmentMonths,
  paymentPlans,
  setPaymentDate,
  eventPackage,
  dueDateOpt,
  setDueDateOpt,
  customDueDate,
  setCustomDueDate,
  showRemoveSeat,
  onRemoveSeat,
  organizationUUID,
}) {
  const paymentPlansEnabled = Boolean(
    AuthService.getOrg()?.[0]?.setting?.paymentPlans
  )

  const handlePaymentSchedule = (e) => {
    const paymentSchedule = e.target.checked ? 'payment-plan' : 'one-time'
    setPaymentSchedule(paymentSchedule)
  }

  const posTerminalProfile = AuthService.getProfile()
  const hasPosTerminal = Boolean(posTerminalProfile?.location)
  const ALLOWED_KEYS = getAssignmentPaymentMethodKeys(
    organizationUUID,
    posTerminalProfile
  )

  const filteredOptions = Object.fromEntries(
    ALLOWED_KEYS.map((key) => [key, PAYMENT_TYPE[key]])
  )

  const [priceHolder, setPriceHolder] = useState(price ?? '')

  // keep local input state in sync when parent price changes (non-package flow)
  useEffect(() => {
    if (!isPackage) setPriceHolder(price ?? '')
  }, [price, isPackage])

  useEffect(() => {
    if (paymentMethod === 'POS' && !hasPosTerminal) {
      setPaymentMethod('')
    }
  }, [paymentMethod, hasPosTerminal, setPaymentMethod])

  useEffect(() => {
    if (
      paymentMethod === 'PAYROLL_DEDUCTION' &&
      !ALLOWED_KEYS.includes('PAYROLL_DEDUCTION')
    ) {
      setPaymentMethod('')
    }
  }, [paymentMethod, setPaymentMethod, ALLOWED_KEYS, organizationUUID])

  useEffect(() => {
    if (!paymentPlansEnabled && paymentSchedule === 'payment-plan') {
      setPaymentSchedule('one-time')
    }
  }, [paymentPlansEnabled, paymentSchedule, setPaymentSchedule])

  const effectivePricePerTicket =
    Number(pricingObject?.pricePerTicket ?? price ?? 0) || 0
  const hasPricePerTicketSet =
    isPackage ||
    (price !== undefined &&
      price !== null &&
      String(price).trim() !== '' &&
      Number.isFinite(Number(price)))

  const sortedPriceTiers = useMemo(() => {
    if (!Array.isArray(priceTiers)) return []
    return [...priceTiers].sort(
      (a, b) => (Number(a?.price) || 0) - (Number(b?.price) || 0)
    )
  }, [priceTiers])

  return (
    <Stack gap={4}>
      <SelectedTickets
        selected={selectedTickets}
        showRemoveSeat={showRemoveSeat}
        onRemoveSeat={onRemoveSeat}
      />
      <Row>
        <Col>
          <div>
            <h5 id="payment-method" className="small small-bold">
              Payment Type
            </h5>
            <Form.Select
              name="payment-method"
              onChange={(e) => setPaymentMethod(e.target.value)}
              value={paymentMethod || ''}
              required
              aria-labelledby="payment-method"
              autoFocus
            >
              <option disabled hidden value="">
                Select payment type
              </option>
              {Object.entries(filteredOptions)
                .sort(([a], [b]) =>
                  getAssignmentPaymentMethodLabel(a).localeCompare(
                    getAssignmentPaymentMethodLabel(b)
                  )
                )
                .map(([option, idx]) => (
                  <option key={idx} value={option}>
                    {getAssignmentPaymentMethodLabel(option)}
                  </option>
                ))}
            </Form.Select>
          </div>
        </Col>
      </Row>
      {allowEditPrice && (
        <Form.Group>
          {isPackage ? (
            <>
              <h5 id="pricing-tier" className="small small-bold">
                Pricing Tier
              </h5>
              <Form.Select
                name="price-tier"
                onChange={(e) => setPrice(e.target.value)}
                value={price || ''}
                required
                aria-labelledby="pricing-tier"
              >
                <option disabled hidden value="">
                  Select a price tier
                </option>
                {sortedPriceTiers.map((key, idx) => (
                  <option key={idx} value={key.price}>
                    {key?.name} ({formatCurrency(key.price)})
                  </option>
                ))}
              </Form.Select>
            </>
          ) : (
            <>
              <Form.Label>Price Per Ticket</Form.Label>
              <InputGroup>
                <InputGroup.Text id="price">$</InputGroup.Text>
                <Form.Control
                  placeholder="Amount"
                  id="price"
                  name="price"
                  pattern="^[0-9.]*$"
                  // we cannot spam stripe with payment intents that are invalid
                  // make sure price is valid - can only enter numbers and decimals
                  onChange={(e) =>
                    setPriceHolder(
                      e.target.value === '' || e.target.validity.valid
                        ? e.target.value
                        : priceHolder
                    )
                  }
                  // only send one payment intent request on blur
                  onBlur={(e) => setPrice(e.target.value)}
                  value={priceHolder}
                  required
                />
              </InputGroup>
            </>
          )}
        </Form.Group>
      )}
      <Stack gap={3}>
        <div>
          <h2 className="normal normal-bold">Payment Schedule</h2>
        </div>
        {paymentPlansEnabled && (
          <Stack
            direction="horizontal"
            gap={3}
            className="horizontal-input-group"
          >
            <Form.Check
              type="checkbox"
              id="payment-plan"
              name="payment-schedule"
              label="Payment Plan"
              checked={paymentSchedule === 'payment-plan'}
              onChange={handlePaymentSchedule}
              disabled={paymentMethod !== 'CARD'}
            />
          </Stack>
        )}
      </Stack>
      {paymentPlansEnabled && paymentSchedule === 'payment-plan' && (
        <>
          <div>
            <h5 id="payment-plan" className="small small-bold">
              Payment Plan
            </h5>
            <Form.Select
              name="payment-plan"
              onChange={(e) => setInstallmentMonths(Number(e.target.value))}
              value={installmentMonths || ''}
              required
              aria-labelledby="payment-plan"
            >
              <option disabled hidden value="">
                Select a payment plan
              </option>
              {[...Array(11)].map((_, idx) => {
                const month = idx + 2
                return (
                  <option key={month} value={month}>
                    {month} Months Payment Plan
                  </option>
                )
              })}
            </Form.Select>
          </div>
          {paymentPlans && Object.keys(paymentPlans)?.length > 0 && (
            <Stack gap={3}>
              <Stack as="ul" gap={3}>
                {Object.values(paymentPlans)?.map((plan, idx) => (
                  <PaymentPlan
                    key={idx}
                    plan={plan}
                    idx={idx}
                    setPaymentDate={setPaymentDate}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </>
      )}
      {paymentMethod === 'INVOICE' && (
        <SelectPaymentDueDate
          dueDateOpt={dueDateOpt}
          setDueDateOpt={setDueDateOpt}
          customDueDate={customDueDate}
          setCustomDueDate={setCustomDueDate}
        />
      )}
      <Stack gap={2}>
        <ul>
          <li className="list list-sm list-item list-item--vertical list-item-lg list-item-lg--sm">
            <Stack as="ul" gap={1}>
              <Stack as="li" direction="horizontal" className="split-row">
                <span>
                  Ticket price ({formatCurrency(effectivePricePerTicket)} x{' '}
                  {formatNumber(pricingObject?.ticketCount)})
                </span>
                <span>
                  {formatCurrency(
                    effectivePricePerTicket * pricingObject?.ticketCount
                  )}
                </span>
              </Stack>
              {pricingObject?.serviceFee !== 0 && (
                <Stack as="li" direction="horizontal" className="split-row">
                  <span>
                    {pricingObject?.isFlatServiceFee
                      ? 'Service Fee (package)'
                      : `Service Fee (${formatCurrency(
                          pricingObject?.serviceFeePerTicket
                        )} x ${formatNumber(pricingObject?.ticketCount)})`}
                  </span>
                  <span>
                    {hasPricePerTicketSet
                      ? formatCurrency(pricingObject?.serviceFee)
                      : '--'}
                  </span>
                </Stack>
              )}
              {Number(pricingObject?.ticketFacilityFee) > 0 && (
                <Stack as="li" direction="horizontal" className="split-row">
                  <span>
                    Facility Fee (
                    {formatCurrency(pricingObject?.ticketFacilityFeePerTicket)}{' '}
                    x {formatNumber(pricingObject?.ticketCount)})
                  </span>
                  <span>
                    {hasPricePerTicketSet
                      ? formatCurrency(pricingObject?.ticketFacilityFee)
                      : '--'}
                  </span>
                </Stack>
              )}
              <Stack as="li" direction="horizontal" className="split-row">
                <span>
                  Sales Tax ({formatCurrency(pricingObject?.salesTaxPerTicket)}{' '}
                  x {formatNumber(pricingObject?.ticketCount)})
                </span>
                <span>{formatCurrency(pricingObject?.salesTax)}</span>
              </Stack>
              {isCardLikeAssignmentPaymentMethod(paymentMethod) && (
                <Stack as="li" direction="horizontal" className="split-row">
                  <span>
                    Processing Fee (estimated) (
                    {formatCurrency(
                      pricingObject?.paymentProcessingFeePerTicket
                    )}{' '}
                    x {formatNumber(pricingObject?.ticketCount)})
                  </span>
                  <span>
                    {formatCurrency(pricingObject?.paymentProcessingFee)}
                  </span>
                </Stack>
              )}
            </Stack>
          </li>
        </ul>
        <ul>
          <li className="list list-sm list-item list-item--vertical list-item-lg list-item-lg--sm">
            <Stack direction="horizontal" className="mb-0 split-row heading">
              <span>Amount to pay</span>
              <span>
                {formatCurrency(
                  getAmountDue(
                    pricingObject,
                    paymentMethod,
                    paymentSchedule === 'payment-plan',
                    paymentPlans
                  )
                )}
              </span>
            </Stack>
          </li>
        </ul>
      </Stack>
    </Stack>
  )
}
